/** Light / dark ("night shift") / match the device. Saved per browser; applied by public/theme.js before first paint. */
import { useEffect, useState } from 'react';

export type ThemeChoice = 'light' | 'dark' | 'auto';
const KEY = 'ch-theme';
const dark = () => typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;

export function themeChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'auto' ? v : 'light';
  } catch {
    return 'light';
  }
}

export function applyTheme(choice: ThemeChoice = themeChoice()) {
  const t = choice === 'auto' ? (dark() ? 'dark' : 'light') : choice;
  document.documentElement.setAttribute('data-theme', t);
}

export function useTheme(): [ThemeChoice, (c: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(themeChoice);
  useEffect(() => {
    applyTheme(choice);
    if (choice !== 'auto' || typeof matchMedia !== 'function') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const on = () => applyTheme('auto');
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [choice]);
  return [
    choice,
    (c) => {
      try {
        localStorage.setItem(KEY, c);
      } catch {
        /* private window: still applies for this visit */
      }
      setChoice(c);
    },
  ];
}
